import { z } from "zod";
import { ABILITY_ID_SCHEMA, type AbilityId } from "@pit/protocol";

/** The flight renderer's existing shape API, adapted from a JSON preset. */
export interface ProjectileVisualProfile {
  readonly shape: "bolt" | "shard" | "orb";
  readonly coreColor: number;
  readonly trailColor: number;
  readonly trailLengthRadii: number;
  readonly trailAlpha: number;
}

/** Impact geometry is selected independently from the projectile in flight. */
export interface ProjectileImpactProfile {
  readonly preset: "rays" | "iceShards" | "fireBurst";
  readonly color: number;
  readonly accentColor: number;
}

export interface ProjectilePresentationProfile {
  readonly flight: ProjectileVisualProfile;
  readonly impact: ProjectileImpactProfile;
}

export interface ProjectileVisualConfig {
  readonly profiles: ReadonlyMap<string, ProjectilePresentationProfile>;
  readonly abilities: ReadonlyMap<AbilityId, string>;
  readonly defaultProfile: ProjectilePresentationProfile;
  readonly resolve: (abilityId: AbilityId | null) => ProjectilePresentationProfile;
}

const COLOR_SCHEMA = z
  .string()
  .length(7)
  .regex(/^#[0-9a-fA-F]{6}$/u, "Expected a #RRGGBB color");
const PROFILE_NAME_SCHEMA = z.string().min(1).max(64);

// These bounds preserve the shipped renderer's small, translucent geometry.
// Adding a preset requires implementing it in the corresponding shared renderer.
const FLIGHT_SCHEMA = z.strictObject({
  preset: z.enum(["bolt", "shard", "orb"]).default("bolt"),
  coreColor: COLOR_SCHEMA.default("#ffffff"),
  trailColor: COLOR_SCHEMA.default("#ffffff"),
  trailLengthRadii: z.number().min(0).max(4).default(2),
  trailAlpha: z.number().min(0).max(0.55).default(0.25),
});
const IMPACT_SCHEMA = z.strictObject({
  preset: z.enum(["rays", "iceShards", "fireBurst"]).default("rays"),
  color: COLOR_SCHEMA.default("#ffffff"),
  accentColor: COLOR_SCHEMA.default("#ffffff"),
});
const PROFILE_SCHEMA = z.strictObject({
  flight: FLIGHT_SCHEMA.optional(),
  impact: IMPACT_SCHEMA.optional(),
});
const CONFIG_SCHEMA = z.strictObject({
  schemaVersion: z.literal(1),
  defaultProfile: PROFILE_NAME_SCHEMA,
  profiles: z.record(PROFILE_NAME_SCHEMA, PROFILE_SCHEMA),
  abilities: z.record(ABILITY_ID_SCHEMA, PROFILE_NAME_SCHEMA),
});

function flightProfile(raw: z.infer<typeof FLIGHT_SCHEMA>): ProjectileVisualProfile {
  return Object.freeze({
    shape: raw.preset,
    coreColor: Number.parseInt(raw.coreColor.slice(1), 16),
    trailColor: Number.parseInt(raw.trailColor.slice(1), 16),
    trailLengthRadii: raw.trailLengthRadii,
    trailAlpha: raw.trailAlpha,
  });
}

function impactProfile(raw: z.infer<typeof IMPACT_SCHEMA>): ProjectileImpactProfile {
  return Object.freeze({
    preset: raw.preset,
    color: Number.parseInt(raw.color.slice(1), 16),
    accentColor: Number.parseInt(raw.accentColor.slice(1), 16),
  });
}

const DEFAULT_FLIGHT = flightProfile(FLIGHT_SCHEMA.parse({}));
const DEFAULT_IMPACT = impactProfile(IMPACT_SCHEMA.parse({}));

/** Expose the map without mutation methods; Object.freeze(Map) alone still
 * permits Map.set(), and callers must not invalidate shared renderer caches. */
function readonlyMap<K, V>(map: Map<K, V>): ReadonlyMap<K, V> {
  const view: ReadonlyMap<K, V> = {
    get size() {
      return map.size;
    },
    get: (key) => map.get(key),
    has: (key) => map.has(key),
    keys: () => map.keys(),
    values: () => map.values(),
    entries: () => map.entries(),
    [Symbol.iterator]: () => map[Symbol.iterator](),
    forEach(callback, thisArg?: unknown) {
      map.forEach((value, key) => {
        callback.call(thisArg, value, key, view);
      });
    },
  };
  return Object.freeze(view);
}

/** Validate once at the presentation boundary. No content, simulation, or IO
 * dependencies: callers supply the set of ability IDs their catalog contains. */
export function loadProjectileVisualConfig(
  raw: unknown,
  knownAbilityIds: ReadonlySet<AbilityId>,
): ProjectileVisualConfig {
  const parsed = CONFIG_SCHEMA.superRefine((config, context) => {
    if (!Object.hasOwn(config.profiles, config.defaultProfile)) {
      context.addIssue({
        code: "custom",
        path: ["defaultProfile"],
        message: `Unknown default projectile profile: ${config.defaultProfile}`,
      });
    }
    for (const [rawId, profileName] of Object.entries(config.abilities)) {
      const abilityId = ABILITY_ID_SCHEMA.parse(rawId);
      if (!knownAbilityIds.has(abilityId)) {
        context.addIssue({
          code: "custom",
          path: ["abilities", rawId],
          message: `Unknown ability ID: ${rawId}`,
        });
      }
      if (!Object.hasOwn(config.profiles, profileName)) {
        context.addIssue({
          code: "custom",
          path: ["abilities", rawId],
          message: `Unknown projectile profile: ${profileName}`,
        });
      }
    }
  }).parse(raw);

  const profiles = new Map<string, ProjectilePresentationProfile>();
  for (const [name, profile] of Object.entries(parsed.profiles)) {
    profiles.set(
      name,
      Object.freeze({
        flight: profile.flight === undefined ? DEFAULT_FLIGHT : flightProfile(profile.flight),
        impact: profile.impact === undefined ? DEFAULT_IMPACT : impactProfile(profile.impact),
      }),
    );
  }
  const defaultProfile = profiles.get(parsed.defaultProfile);
  if (defaultProfile === undefined)
    throw new Error("Validated default projectile profile is missing");

  const abilities = new Map<AbilityId, string>();
  const resolved = new Map<AbilityId, ProjectilePresentationProfile>();
  for (const [rawId, profileName] of Object.entries(parsed.abilities)) {
    const abilityId = ABILITY_ID_SCHEMA.parse(rawId);
    const profile = profiles.get(profileName);
    if (profile === undefined) throw new Error("Validated projectile profile is missing");
    abilities.set(abilityId, profileName);
    resolved.set(abilityId, profile);
  }
  return Object.freeze({
    profiles: readonlyMap(profiles),
    abilities: readonlyMap(abilities),
    defaultProfile,
    resolve: (abilityId: AbilityId | null): ProjectilePresentationProfile =>
      abilityId === null ? defaultProfile : (resolved.get(abilityId) ?? defaultProfile),
  });
}
